import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { QrCode, Upload, Clock, User, Trash2 } from 'lucide-react';

export default function PaymentQRManagement() {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentQR, setCurrentQR] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchPaymentQR();
  }, []);

  const fetchPaymentQR = async () => {
    try {
      const response = await axios.get(`${API_URL}/payment-qr`, {
        headers: getAuthHeaders()
      });
      setCurrentQR(response.data);
    } catch (error) {
      toast.error('Failed to fetch Payment QR');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a QR image to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('qr_image', selectedFile);

      const response = await axios.put(`${API_URL}/payment-qr`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Payment QR updated successfully');
      setCurrentQR(response.data.value);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Payment QR');
    } finally {
      setUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';

  if (!isAdmin) {
    return (
      <Sidebar>
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-12">
            <p className="text-slate-500">You don't have permission to access this page.</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payment QR Management
          </h1>
          <p className="text-slate-600 mt-2">Manage the payment QR code visible to Agencies and Master Agencies</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Current QR Display */}
            {currentQR?.image_data && (
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <QrCode className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Current Payment QR</h2>
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <img 
                      src={currentQR.image_data} 
                      alt="Current Payment QR" 
                      className="max-w-[250px] rounded-lg border border-slate-200"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-sm text-emerald-800 mb-2">
                        This QR code is currently visible to all Agencies and Master Agencies when they click the "Payment QR" button.
                      </p>
                      {currentQR.updated_at && (
                        <div className="mt-3 pt-3 border-t border-emerald-200 space-y-1 text-xs text-emerald-600">
                          <p className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Updated: {new Date(currentQR.updated_at).toLocaleString()}
                          </p>
                          {currentQR.updated_by_name && (
                            <p className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              By: {currentQR.updated_by_name}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upload New QR */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {currentQR?.image_data ? 'Update Payment QR' : 'Upload Payment QR'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="qr-upload">Select QR Image</Label>
                  <div className="mt-2">
                    <input
                      id="qr-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      data-testid="qr-file-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Supported formats: JPG, PNG, GIF. Max size: 5MB
                    </p>
                  </div>
                </div>

                {/* Preview */}
                {previewUrl && (
                  <div className="border border-dashed border-slate-300 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-sm font-medium text-slate-700">Preview:</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearSelection}
                        className="text-red-600 hover:text-red-700 -mt-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <img 
                      src={previewUrl} 
                      alt="QR Preview" 
                      className="max-w-[200px] rounded-lg border border-slate-200"
                    />
                    <p className="text-xs text-slate-500 mt-2">{selectedFile?.name}</p>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="upload-qr-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : (currentQR?.image_data ? 'Update QR' : 'Upload QR')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">How it works</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Upload your payment QR code image (UPI, Bank transfer, etc.)</li>
                <li>Agencies and Master Agencies will see this QR when they click "Payment QR"</li>
                <li>Only Admin and Sub Admin can modify the QR image</li>
                <li>Use a clear, high-quality QR image for best results</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
}
